import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IEntityChatMember, IEntityChatMessage } from 'src/components/EntityChatView/interface';
import { getQuoteChatMembers, getQuoteChatMessages, markQuoteChatAsRead, sendCustomerMessage } from 'src/services/ChatService';

export interface QuoteChatState {
    messages: IEntityChatMessage[];
    members: IEntityChatMember[];
    isLoading: boolean;
    isSending: boolean;
    isMembersLoading: boolean;
    // In-flight guards. ChatView is mounted TWICE on the quote detail page (the Messages tab and
    // the Messages widget) and both share this single slice, so every lifecycle fetch used to go
    // out twice. These flags back the `condition` option on the thunks below, which drops the
    // duplicate dispatch instead of firing a second identical request.
    isMarkingAsRead: boolean;
    isCatchingUp: boolean;
    hasMoreMessages: boolean;
    lastMessageId: string | number | null;
    chatGroupId: string;
    unreadCount: number;
    hiddenMessageIds: (string | number)[];
    isSocketRoomJoined: boolean;
}

const initialState: QuoteChatState = {
    messages: [],
    members: [],
    isLoading: false,
    isSending: false,
    isMembersLoading: false,
    isMarkingAsRead: false,
    isCatchingUp: false,
    hasMoreMessages: false,
    lastMessageId: null,
    chatGroupId: '',
    unreadCount: 0,
    hiddenMessageIds: [],
    isSocketRoomJoined: false,
};

/**
 * Normalize a raw API message into IEntityChatMessage shape
 */
const normalizeMessage = (raw: any): IEntityChatMessage => {
    const senderData = raw?.sender || raw?.userInfo || {};

    let content = '';

    if (typeof raw?.content === 'object' && raw?.content !== null) {
        content = raw.content?.message || '';
    } else {
        content = raw?.content || raw?.message || '';
    }

    return {
        id: raw?.id?.toString() || `${Date.now()}`,
        content,
        userInfo: {
            id: raw?.senderId || senderData?.id || '',
            firstName: senderData?.firstName || '',
            lastName: senderData?.lastName || '',
            imageUrl: senderData?.imageUrl || '',
            image: senderData?.imageUrl && senderData?.image ? `${senderData.imageUrl}${senderData.image}` : senderData?.image || '',
            user_status: senderData?.user_status || 'offline',
        },
        createdAt: raw?.createdAt || new Date().toISOString(),
        isUnread: raw?.isUnread || false,
        isOptimistic: false,
        hasError: false,
        messageType: raw?.messageType || 'text',
        attachments: raw?.attachments || [],
        senderType: raw?.senderType || 'user',
        chatType: raw?.chatType || 'customer',
    };
};

/**
 * Merge one raw incoming message into state, deduplicating by id and reconciling the optimistic
 * copy of a message this client just sent. Shared by the socket handler and the catch-up fetch so
 * both paths apply identical dedupe rules.
 */
const mergeIncomingMessage = (state: QuoteChatState, raw: any) => {
    const normalized = normalizeMessage(raw);

    const existingIndex = state.messages.findIndex((m) => m.id?.toString() === normalized.id?.toString());

    if (existingIndex >= 0 && !state.messages[existingIndex].isOptimistic) {
        return;
    }

    if (existingIndex >= 0 && state.messages[existingIndex].isOptimistic) {
        state.messages[existingIndex] = normalized;

        return;
    }

    const senderId = raw?.senderId?.toString();
    const optimisticIndex = state.messages.findIndex(
        (m) => m.isOptimistic && !m.hasError && m.userInfo?.id?.toString() === senderId && m.content?.trim() === normalized.content?.trim() && m.senderType === 'customer'
    );

    if (optimisticIndex >= 0) {
        state.messages[optimisticIndex] = normalized;

        return;
    }

    state.messages.push(normalized);
};

export const fetchQuoteChatMessages = createAsyncThunk(
    'quoteChat/fetchMessages',
    async ({ quoteId, lastId }: { quoteId: string | number; lastId?: string | number | null }) => {
        const response: any = await getQuoteChatMessages(quoteId, lastId ? { lastId } : undefined);
        const data = response?.data || response;
        const messages: any[] = data?.messages || data?.data?.messages || [];
        const hasMore: boolean = data?.hasMore ?? data?.data?.hasMore ?? data?.pagination?.hasMore ?? false;
        const nextLastId = data?.pagination?.nextCursor ?? data?.data?.pagination?.nextCursor ?? null;
        const chatGroupId: string = data?.chatGroupId || data?.data?.chatGroupId || '';

        const normalizedMessages = messages.map(normalizeMessage).reverse();

        return { messages: normalizedMessages, hasMore, lastId: nextLastId, isPagination: !!lastId, chatGroupId };
    },
    {
        // Only the initial (non-paginated) page is deduplicated — two ChatView instances mount in
        // the same commit and both ask for page 1. Pagination requests carry a distinct `lastId`
        // and must always go through.
        condition: ({ lastId }, { getState }) => {
            if (lastId) return true;

            return !(getState() as any).quoteChat.isLoading;
        },
    }
);

export const fetchQuoteChatMembers = createAsyncThunk(
    'quoteChat/fetchMembers',
    async (quoteId: string | number) => {
        const response: any = await getQuoteChatMembers(quoteId);
        const data = response?.data || response;
        const members: any[] = data?.members || data?.data?.members || data || [];

        return members.map((m: any) => ({
            id: m?.id,
            firstName: m?.firstName || '',
            lastName: m?.lastName || '',
            imageUrl: m?.imageUrl || '',
            image: m?.image || '',
            user_status: m?.user_status || 'offline',
            isOnline: m?.user_status === 'online',
        })) as IEntityChatMember[];
    },
    {
        condition: (_quoteId, { getState }) => !(getState() as any).quoteChat.isMembersLoading,
    }
);

export const markQuoteChatMessagesAsRead = createAsyncThunk(
    'quoteChat/markAsRead',
    async (quoteId: string | number) => {
        await markQuoteChatAsRead(quoteId);
    },
    {
        // Fired from several places at once on open: the widget's isActive effect, the autofocus
        // handler it triggers 50ms later, and the Messages tab when it becomes active. They all
        // write the same lastReadAt, so collapsing concurrent calls loses nothing.
        condition: (_quoteId, { getState }) => !(getState() as any).quoteChat.isMarkingAsRead,
    }
);

/**
 * Re-fetch the newest page and merge it in (deduped by id).
 * Covers messages that landed in the gap between the mount fetch and the socket room join, and
 * after a reconnect. Guarded so the tab and the widget — which join the same room in the same
 * commit — do not both issue it.
 */
export const catchUpQuoteChatMessages = createAsyncThunk(
    'quoteChat/catchUpMessages',
    async (quoteId: string | number) => {
        try {
            const response: any = await getQuoteChatMessages(quoteId);
            const data = response?.data || response;
            const rawMessages: any[] = data?.messages || data?.data?.messages || [];

            // API returns DESC (newest first). Reverse to ASC so the messages append in order.
            return [...rawMessages].reverse();
        } catch (error) {
            // Non-fatal — the thread already has the mount fetch's page and live socket updates.
            // Logged (not toasted) because this runs in the background on every room join.
            console.error('Chat catch-up fetch failed:', { quoteId, error });
            throw error;
        }
    },
    {
        condition: (_quoteId, { getState }) => !(getState() as any).quoteChat.isCatchingUp,
    }
);

const quoteChatSlice = createSlice({
    name: 'quoteChat',
    initialState,
    reducers: {
        addQuoteSocketMessage: (state, action: PayloadAction<any>) => {
            mergeIncomingMessage(state, action.payload);
        },
        incrementQuoteUnreadCount: (state) => {
            state.unreadCount += 1;
        },
        updateQuoteMemberStatus: (state, action: PayloadAction<{ memberId: string | number; status: string }>) => {
            const { memberId, status } = action.payload;
            const member = state.members.find((m) => m.id?.toString() === memberId?.toString());

            if (member) {
                member.user_status = status;
                member.isOnline = status === 'online';
            }
        },
        resetQuoteChat: () => initialState,
        setIsQuoteSocketRoomJoined: (state, action: PayloadAction<boolean>) => {
            state.isSocketRoomJoined = action.payload;
        },
        _addQuoteOptimisticMessage: (state, action: PayloadAction<IEntityChatMessage>) => {
            state.messages.push(action.payload);
        },
        _markQuoteMessageError: (state, action: PayloadAction<string>) => {
            const msg = state.messages.find((m) => m.id === action.payload);

            if (msg) {
                msg.hasError = true;
            }
        },
        hideQuoteMessage: (state, action: PayloadAction<string | number>) => {
            if (!state.hiddenMessageIds.includes(action.payload)) {
                state.hiddenMessageIds.push(action.payload);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchQuoteChatMessages.pending, (state, action) => {
                const isPagination = action.meta.arg.lastId != null;

                if (!isPagination) {
                    state.isLoading = true;
                }
            })
            .addCase(fetchQuoteChatMessages.fulfilled, (state, action) => {
                state.isLoading = false;
                const { messages, hasMore, lastId, isPagination, chatGroupId } = action.payload;

                if (isPagination) {
                    const existingIds = new Set(state.messages.map((m) => m.id?.toString()));
                    const newMessages = messages.filter((m) => !existingIds.has(m.id?.toString()));
                    state.messages = [...newMessages, ...state.messages];
                } else {
                    state.messages = messages;
                    state.unreadCount = messages.filter((m) => m.isUnread).length;
                }

                state.hasMoreMessages = hasMore;
                state.lastMessageId = lastId;

                if (!isPagination && chatGroupId) {
                    state.chatGroupId = chatGroupId;
                }
            })
            .addCase(markQuoteChatMessagesAsRead.pending, (state) => {
                state.isMarkingAsRead = true;
            })
            .addCase(markQuoteChatMessagesAsRead.fulfilled, (state) => {
                state.isMarkingAsRead = false;
                state.unreadCount = 0;
            })
            .addCase(markQuoteChatMessagesAsRead.rejected, (state) => {
                state.isMarkingAsRead = false;
            })
            .addCase(catchUpQuoteChatMessages.pending, (state) => {
                state.isCatchingUp = true;
            })
            .addCase(catchUpQuoteChatMessages.fulfilled, (state, action) => {
                state.isCatchingUp = false;
                (action.payload || []).forEach((raw: any) => mergeIncomingMessage(state, raw));
            })
            .addCase(catchUpQuoteChatMessages.rejected, (state) => {
                state.isCatchingUp = false;
            })
            .addCase(fetchQuoteChatMessages.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchQuoteChatMembers.pending, (state) => {
                state.isMembersLoading = true;
            })
            .addCase(fetchQuoteChatMembers.fulfilled, (state, action) => {
                state.isMembersLoading = false;
                state.members = action.payload;
            })
            .addCase(fetchQuoteChatMembers.rejected, (state) => {
                state.isMembersLoading = false;
            });
    },
});

export const { addQuoteSocketMessage, incrementQuoteUnreadCount, updateQuoteMemberStatus, resetQuoteChat, setIsQuoteSocketRoomJoined, hideQuoteMessage } = quoteChatSlice.actions;

export const sendQuoteChatMessage = createAsyncThunk(
    'quoteChat/sendMessage',
    async ({ quoteId, content, files, currentUser }: { quoteId: string | number; content: string; files?: File[]; currentUser: any }, { dispatch, getState, rejectWithValue }) => {
        const chatGroupId = (getState() as any).quoteChat.chatGroupId || '';
        const tempId = `temp-${Date.now()}`;

        const optimisticAttachments =
            files && files.length > 0
                ? files.map((file) => ({
                      orignalName: file.name,
                      name: file.name,
                      fileSize: file.size,
                      fileType: file.type.startsWith('image/') ? 'image' : 'file',
                      _blobUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
                  }))
                : [];

        const optimisticMessage: IEntityChatMessage = {
            id: tempId,
            content,
            userInfo: {
                id: currentUser?.id || '',
                firstName: currentUser?.firstName || currentUser?.first_name || '',
                lastName: currentUser?.lastName || currentUser?.last_name || '',
                imageUrl: currentUser?.imageUrl || '',
                image: currentUser?.image || '',
            },
            createdAt: new Date().toISOString(),
            isOptimistic: true,
            hasError: false,
            messageType: files && files.length > 0 ? 'file' : 'text',
            attachments: optimisticAttachments,
            _fileObjects: files,
            senderType: 'customer',
            chatType: 'customer',
        };

        dispatch(quoteChatSlice.actions._addQuoteOptimisticMessage(optimisticMessage));

        try {
            const messageType = files && files.length > 0 ? files[0]?.type || 'file' : 'text';
            const authorizationToken = localStorage.getItem('token') || undefined;

            await sendCustomerMessage(
                {
                    entityId: Number(quoteId),
                    entityType: 'quote',
                    content: { message: content },
                    participantType: 'customer',
                    senderId: currentUser?.id || '',
                    groupId: chatGroupId || '',
                    chatType: 'customer',
                    messageType,
                },
                files && files.length > 0 ? files : undefined,
                authorizationToken
            );

            return { tempId };
        } catch (error: any) {
            dispatch(quoteChatSlice.actions._markQuoteMessageError(tempId));

            return rejectWithValue({ tempId, error: error?.message });
        }
    }
);

export default quoteChatSlice.reducer;
